/**
 * Workspace-local message catalogue (de + en).
 *
 * Reusable workspace chrome must never hard-code a user-facing string. Shell
 * message files (`app/messages/**`) are owned by another lane, so this catalogue
 * is kept local and typed; it is consumed via the shell locale store's current
 * language code (`"de" | "en"`) without editing shared files.
 */

export type WorkspaceLocale = "de" | "en";

export type WorkspaceMessageKey =
  | "workspaceLabel"
  | "workspaceCanvas"
  | "panelRegion"
  | "collapse"
  | "expand"
  | "enterFullscreen"
  | "exitFullscreen"
  | "moveHint"
  | "grabbed"
  | "released"
  | "resetLayout"
  | "saveLayout"
  | "roleDefault"
  | "presetPicker"
  | "presetRoleDefault"
  | "workspaceBarPrefix"
  | "closeWindow"
  | "deviceModeLabel"
  | "deviceDesktop"
  | "deviceTablet"
  | "deviceMobile"
  | "floatingWindow"
  | "panelTablist"
  | "openPanel"
  | "searchPanels"
  | "noPanels"
  | "panelOpened"
  | "panelGroupCommand"
  | "panelGroupPicture"
  | "panelGroupSupport"
  | "panelGroupContext"
  | "panelGroupOther"
  | "quickPanels"
  | "emptyContent"
  | "removePanel"
  | "demoHeading"
  | "demoIntro"
  | "openFloating"
  | "workspaceForbidden"
  | "workspaceUnavailable"
  | "workspaceLoading"
  | "workspaceIncidentRequired"
  | "workspaceIncidentRequiredDescription"
  | "workspaceNoActiveIncidents"
  | "workspaceNoActiveIncidentsDescription"
  | "workspaceIncidentContextLoading"
  | "workspaceIncidentContextError"
  | "workspaceIncidentContextRetry"
  | "workspaceIncidentContextForbidden"
  | "workspaceComponentError";

type Catalogue = Readonly<Record<WorkspaceMessageKey, string>>;

const en: Catalogue = {
  workspaceLabel: "Operational workspace",
  workspaceCanvas: "Panel canvas",
  panelRegion: "Panel",
  collapse: "Collapse panel",
  expand: "Expand panel",
  enterFullscreen: "Maximise panel",
  exitFullscreen: "Restore panel",
  moveHint:
    "Arrow keys move this panel. Hold Shift and use arrow keys to resize it.",
  grabbed: "Panel grabbed. Use arrow keys to move, Enter to drop.",
  released: "Panel dropped.",
  resetLayout: "Reset layout",
  saveLayout: "Save layout",
  roleDefault: "Role default",
  presetPicker: "Choose role layout",
  presetRoleDefault: "your role",
  workspaceBarPrefix: "Workspace:",
  closeWindow: "Close window",
  deviceModeLabel: "Presentation mode",
  deviceDesktop: "Desktop",
  deviceTablet: "Tablet",
  deviceMobile: "Mobile",
  floatingWindow: "Floating tool window",
  panelTablist: "Workspace panels",
  openPanel: "Open panel",
  searchPanels: "Search panels",
  noPanels: "No matching panels.",
  panelOpened: "Open",
  panelGroupCommand: "Command & execution",
  panelGroupPicture: "Situation & evidence",
  panelGroupSupport: "Resources & support",
  panelGroupContext: "Context",
  panelGroupOther: "Other",
  quickPanels: "Quick access",
  emptyContent: "No content is mounted in this panel.",
  removePanel: "Remove panel",
  demoHeading: "Workspace framework demo",
  demoIntro:
    "A self-contained example wiring placeholder panels into the configurable workspace.",
  openFloating: "Open floating tool",
  workspaceForbidden: "You do not have permission to view this panel.",
  workspaceUnavailable: "This panel type is no longer available.",
  workspaceLoading: "Loading panel…",
  workspaceIncidentRequired: "Select an active incident to continue.",
  workspaceIncidentRequiredDescription:
    "Choose an incident from Active incidents to display this panel's information.",
  workspaceNoActiveIncidents: "No active incidents are available.",
  workspaceNoActiveIncidentsDescription:
    "Nothing is running in this organization and role. Record one in Incident intake, or switch your work context.",
  workspaceIncidentContextLoading: "Loading active incidents…",
  workspaceIncidentContextError: "Active incidents could not be loaded.",
  workspaceIncidentContextRetry: "Retry",
  workspaceIncidentContextForbidden:
    "Your current role and organization are not authorized to see active incidents.",
  workspaceComponentError: "This panel failed to load.",
};

const de: Catalogue = {
  workspaceLabel: "Einsatz-Arbeitsbereich",
  workspaceCanvas: "Panel-Fläche",
  panelRegion: "Panel",
  collapse: "Panel einklappen",
  expand: "Panel ausklappen",
  enterFullscreen: "Panel maximieren",
  exitFullscreen: "Panel wiederherstellen",
  moveHint:
    "Pfeiltasten verschieben dieses Panel. Mit gedrückter Umschalttaste ändern die Pfeiltasten die Größe.",
  grabbed: "Panel aufgenommen. Pfeiltasten zum Verschieben, Enter zum Ablegen.",
  released: "Panel abgelegt.",
  resetLayout: "Layout zurücksetzen",
  saveLayout: "Layout speichern",
  roleDefault: "Rollen-Standard",
  presetPicker: "Rollen-Layout wählen",
  presetRoleDefault: "Ihre Funktion",
  workspaceBarPrefix: "Arbeitsbereich:",
  closeWindow: "Fenster schließen",
  deviceModeLabel: "Darstellungsmodus",
  deviceDesktop: "Desktop",
  deviceTablet: "Tablet",
  deviceMobile: "Mobil",
  floatingWindow: "Schwebendes Werkzeugfenster",
  panelTablist: "Arbeitsbereich-Panels",
  openPanel: "Panel öffnen",
  searchPanels: "Panels suchen",
  noPanels: "Keine passenden Panels.",
  panelOpened: "Geöffnet",
  panelGroupCommand: "Führung & Ausführung",
  panelGroupPicture: "Lage & Nachweise",
  panelGroupSupport: "Ressourcen & Unterstützung",
  panelGroupContext: "Kontext",
  panelGroupOther: "Weitere",
  quickPanels: "Schnellzugriff",
  emptyContent: "In diesem Panel ist kein Inhalt eingebunden.",
  removePanel: "Panel entfernen",
  demoHeading: "Demo des Arbeitsbereich-Frameworks",
  demoIntro:
    "Ein eigenständiges Beispiel, das Platzhalter-Panels in den konfigurierbaren Arbeitsbereich einbindet.",
  openFloating: "Schwebendes Werkzeug öffnen",
  workspaceForbidden: "Sie haben keine Berechtigung, dieses Panel anzuzeigen.",
  workspaceUnavailable: "Dieser Panel-Typ ist nicht mehr verfügbar.",
  workspaceLoading: "Panel wird geladen …",
  workspaceIncidentRequired: "Wählen Sie einen aktiven Einsatz aus, um fortzufahren.",
  workspaceIncidentRequiredDescription:
    "Wählen Sie unter Aktive Einsätze einen Einsatz aus, um die Informationen dieses Panels anzuzeigen.",
  workspaceNoActiveIncidents: "Es sind keine aktiven Einsätze verfügbar.",
  workspaceNoActiveIncidentsDescription:
    "In dieser Organisation und Rolle läuft derzeit kein Einsatz. Erfassen Sie einen in der Einsatzannahme oder wechseln Sie den Arbeitskontext.",
  workspaceIncidentContextLoading: "Aktive Einsätze werden geladen …",
  workspaceIncidentContextError: "Aktive Einsätze konnten nicht geladen werden.",
  workspaceIncidentContextRetry: "Erneut versuchen",
  workspaceIncidentContextForbidden:
    "Ihre aktuelle Rolle und Organisation sind nicht berechtigt, aktive Einsätze zu sehen.",
  workspaceComponentError: "Dieses Panel konnte nicht geladen werden.",
};

const catalogues: Readonly<Record<WorkspaceLocale, Catalogue>> = { de, en };

export function isWorkspaceLocale(value: string | undefined): value is WorkspaceLocale {
  return value === "de" || value === "en";
}

/** Resolves a chrome string for the active locale, falling back to English. */
export function workspaceText(locale: WorkspaceLocale, key: WorkspaceMessageKey): string {
  return catalogues[locale][key] ?? en[key];
}
